import { createSelector } from '@reduxjs/toolkit';
import { APP_SECTORS_CODES } from '@constants/constants';
import urlsService from '@/services/urls-service';
import { SecurityScope } from '@/utils/acl-utils';
import { baseApi } from './baseApi';
/**
 * 1: RTK does not see an active subscription on this endpoint because it's generated hooks `useXXXX` are not called.
 * The default behavior then is to clean up the data after 60 seconds, this triggers a re-render of components,
 * that in turn get whatever `Can` / `Cannot` are returning as default fallback
 */

export interface IUserWrapper {
  isBusinessAccount?: boolean;
  isOciSession?: boolean;
  user: IUser;
  customer: ICustomer;
  accessControlList: SecurityScope;
}

export interface IUser {
  userId: number;
  userName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  mobile?: string;
  accountAdmin: boolean;
  productPermission: boolean;
  onAccount: boolean;
  industryGroupCode: string;
  businessUnitCode: string;
  segmentCode: string;
  preferences?: JSONValue;
  showPrices?: boolean;
  portalId?: number;
}

export interface ICustomer {
  isBusinessAccount: boolean;
  customerNr: string;
  defaultShippingCost: IShipmentCost;
  name: string;
  address: ICustomerAddress;
  contact: string;
  phone: string;
  registrationNr: string;
  vatRegistrationNr: string;
}

export interface IShipmentCost {
  exTax: Tax;
}

export interface Tax {
  numeric: number;
  formatted: string;
}

export interface ICustomerAddress {
  code: string;
  name: string;
  address: string;
  postalCode: string;
  city: string;
  countryCode: string;
}

const CACHE_TAG = 'CURRENT_USER';

export const currentUserApi = baseApi.enhanceEndpoints({ addTagTypes: [CACHE_TAG] }).injectEndpoints({
  endpoints: (builder) => ({
    getCurrentUser: builder.query<IUserWrapper, void>({
      query: () => ({
        url: urlsService.getUrl('/user'),
        method: 'GET'
      }),
      keepUnusedDataFor: 99999, // [1]
      providesTags: [CACHE_TAG]
    }),
    updateUser: builder.mutation<any, any>({
      query(data) {
        // Filter data
        const {
          accessControlList,
          user: { accountAdmin, businessUnitCode, industryGroupCode, isBusinessAccount, productPermission, segmentCode, ...filteredUser },
          ...filteredData
        } = data;

        const requestData = {
          ...filteredData,
          user: filteredUser
        };

        return {
          url: urlsService.getUrl('/user'),
          method: 'POST',
          body: requestData
        };
      },
      invalidatesTags: [CACHE_TAG]
    }),
    updatePassword: builder.mutation<any, any>({
      query({ oldPassword, newPassword, repeatPassword }) {
        return {
          url: urlsService.getUrl('/users/changepassword'),
          method: 'POST',
          body: { oldPassword, newPassword, repeatPassword }
        };
      },
      invalidatesTags: [CACHE_TAG]
    })
  }),
  overrideExisting: true
});

export const { useGetCurrentUserQuery, useLazyGetCurrentUserQuery, useUpdateUserMutation, useUpdatePasswordMutation } = currentUserApi;
export const currentUserSelector = currentUserApi.endpoints.getCurrentUser.select();
export const selectCurrentUser = createSelector([currentUserSelector], (state) => state?.data);
export const selectUser = createSelector([currentUserSelector], (state) => state?.data?.user);
export const selectAccessControlList = createSelector([currentUserSelector], (state) => state?.data?.accessControlList);
export const selectCustomer = createSelector([currentUserSelector], (state) => state?.data?.customer);
export const selectUserIsLoggedIn = createSelector([currentUserSelector], (state) => !!state?.data?.user);
export const selectCanSetLineOnAccount = createSelector(
  [selectCurrentUser],
  (currentUser) =>
    currentUser?.user?.businessUnitCode === APP_SECTORS_CODES.FACILITY &&
    !!currentUser?.isBusinessAccount &&
    (currentUser?.user?.showPrices ?? true)
);
